import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import passengerService from '../../services/passengerService';

// Async thunks
export const fetchPassengers = createAsyncThunk(
  'passengers/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await passengerService.getAll();
      return response.success ? response.data : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch passengers');
    }
  }
);

export const fetchPassengerById = createAsyncThunk(
  'passengers/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await passengerService.getById(id);
      return response.success ? response.data : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch passenger');
    }
  }
);

export const createPassenger = createAsyncThunk(
  'passengers/create',
  async (passengerData, { rejectWithValue }) => {
    try {
      const response = await passengerService.create(passengerData);
      return response.success ? response.data : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create passenger');
    }
  }
);

export const updatePassenger = createAsyncThunk(
  'passengers/update',
  async ({ id, passengerData }, { rejectWithValue }) => {
    try {
      const response = await passengerService.update(id, passengerData);
      return response.success ? response.data : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update passenger');
    }
  }
);

export const deletePassenger = createAsyncThunk(
  'passengers/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await passengerService.delete(id);
      return response.success ? id : rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete passenger');
    }
  }
);

const passengerSlice = createSlice({
  name: 'passengers',
  initialState: {
    items: [],
    selectedPassenger: null,
    loading: false,
    error: null,
    successMessage: null
  },
  reducers: {
    clearPassengerError: (state) => {
      state.error = null;
    },
    clearPassengerSuccess: (state) => {
      state.successMessage = null;
    },
    setSelectedPassenger: (state, action) => {
      state.selectedPassenger = action.payload;
    },
    clearSelectedPassenger: (state) => {
      state.selectedPassenger = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all passengers
      .addCase(fetchPassengers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPassengers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchPassengers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch passenger by ID
      .addCase(fetchPassengerById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPassengerById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPassenger = action.payload;
      })
      .addCase(fetchPassengerById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create passenger
      .addCase(createPassenger.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createPassenger.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.successMessage = 'Passenger saved successfully';
      })
      .addCase(createPassenger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update passenger
      .addCase(updatePassenger.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updatePassenger.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.selectedPassenger = action.payload;
        state.successMessage = 'Passenger updated successfully';
      })
      .addCase(updatePassenger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete passenger
      .addCase(deletePassenger.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePassenger.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(p => p.id !== action.payload);
        if (state.selectedPassenger?.id === action.payload) {
          state.selectedPassenger = null;
        }
        state.successMessage = 'Passenger deleted successfully';
      })
      .addCase(deletePassenger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  clearPassengerError,
  clearPassengerSuccess,
  setSelectedPassenger,
  clearSelectedPassenger
} = passengerSlice.actions;

export default passengerSlice.reducer;
